import {
  BlockNoteEditor,
  BlockSchema,
  InlineContentSchema,
  insertOrUpdateBlock,
  StyleSchema,
} from '@blocknote/core';
import {
  createReactBlockSpec,
  DefaultReactSuggestionItem,
  ReactCustomBlockRenderProps,
} from '@blocknote/react';
import { getEditorMode } from 'utils/createEditorWithMode';
import { ReactElement } from 'react';
import { MultipleChoiceIcon } from 'assets/icons/v2';
import { useBlockDelete } from 'hooks/useBlockDelete';
import { TeacherMCBlock } from './teacher/TeacherMCBlock';
import { StudentMCBlock } from './student/StudentMCBlock';
import { ReviewMCBlock } from './reviewer/ReviewMCBlock';
import { BlockBorderBox } from '../common/BlockBorderBox';

export type MultipleChoiceBlockProps = ReactCustomBlockRenderProps<
  (typeof MultipleChoiceBlock)['config'],
  InlineContentSchema,
  StyleSchema
>;

const propSchema = {
  question: {
    type: 'string',
    default: '',
  },
  options: {
    default: JSON.stringify([
      { text: '', isCorrect: false, isChecked: false },
      { text: '', isCorrect: true, isChecked: false },
      { text: '', isCorrect: false, isChecked: false },
      { text: '', isCorrect: false, isChecked: false },
    ]),
  },
  isSubmitted: {
    type: 'boolean',
    default: false,
  },
};

const MultipleChoiceBlockContent = ({
  props,
}: {
  props: MultipleChoiceBlockProps;
}): ReactElement => {
  const { editor, block } = props;
  const mode = getEditorMode(editor);

  const { isDeleting, handleDelete } = useBlockDelete({
    editor,
    block,
    name: 'Multiple-Choice Frage',
  });

  const handleSeparatorClick = (): void => {
    editor.focus();
    editor.setTextCursorPosition(block, 'end');
    // Simulate Enter key press to create a new block
    const enterEvent = new KeyboardEvent('keydown', {
      key: 'Enter',
      code: 'Enter',
      keyCode: 13,
      which: 13,
      bubbles: true,
      cancelable: true,
      composed: true,
    });
    editor.domElement?.dispatchEvent(enterEvent);
    editor.openSuggestionMenu('/');
  };

  const getBlockByMode = (): ReactElement | null => {
    switch (mode) {
      case 'author':
        return <TeacherMCBlock {...props} onDelete={handleDelete} />;
      case 'student':
        return <StudentMCBlock {...props} />;
      case 'review':
        return <ReviewMCBlock {...props} />;
      default:
        return null;
    }
  };

  return (
    <BlockBorderBox
      isDeleting={isDeleting}
      onSeparatorClick={mode === 'author' ? handleSeparatorClick : undefined}
      shouldShowSeparator={mode === 'author'}
      marginTop={{ base: '15px', md: '20px' }}
      marginBottom={{ base: '10px', md: '15px' }}
    >
      {getBlockByMode()}
    </BlockBorderBox>
  );
};

const MultipleChoiceBlock = createReactBlockSpec(
  {
    type: 'multipleChoice',
    propSchema,
    content: 'inline',
  },
  {
    render: (props) => <MultipleChoiceBlockContent props={props} />,
  }
);

const createMultipleChoiceSlashItem = <T extends BlockSchema>(
  editor: BlockNoteEditor<T>
): DefaultReactSuggestionItem => ({
  title: 'Multiple-Choice Frage',
  onItemClick: () => {
    const insertedBlock = insertOrUpdateBlock(editor, {
      type: 'multipleChoice',
    });

    // Add a paragraph block after the inserted block
    editor.insertBlocks(['paragraph'], insertedBlock, 'after');
  },
  aliases: ['multipleChoice', 'mc'],
  group: 'Interaktiv',
  icon: <MultipleChoiceIcon width={18} height={18} />,
  subtext: 'Frage mit einer oder mehreren Antwortmöglichkeiten',
});

export { MultipleChoiceBlock, createMultipleChoiceSlashItem };
